import { FC, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { CustomButton, CustomTextBox } from "../../../components";
import { formatCurrency, pxToRem } from "../../../util";
import { Colors } from "../../../configs/colors";
import { useAppSelector, useAppDispatch } from "../../../hooks";
import { voidInvoiceAction } from "../../../redux/actions/voidInvoiceAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { resetVoidInvoiceState } from "../../../redux/slice/invoiceSlice";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import { RefundType } from "../../../typings/enum/RefundType";
import { useParams } from "react-router-dom";
import { CustomSelectBox } from "../../../components/atoms/custom-select-box/custom-select-box";
import { paymentTypeLabels } from "../../../typings/enum/paymentTypes";
import ManagerCodeModal from "../../../components/common/manager-code-modal/ManagerCodeModal";

interface IVoidInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  onRefundSuccess: () => void;
  voidTypeIndex: number;
  setVoidTypeIndex: (value: number) => void;
  approvedManagerId?: number;
}

const VoidInvoiceModal: FC<IVoidInvoiceModalProps> = ({
  open,
  onClose,
  onRefundSuccess,
  voidTypeIndex,
  setVoidTypeIndex,
  approvedManagerId,
}) => {
  const [reason, setReason] = useState("");
  const [refundMethod, setRefundMethod] = useState<RefundType>(
    RefundType.STORE_CREDIT
  );

  const params = useParams();
  const { selectedSession, cartHistorySelectedRows, billPaymentDetails } =
    useAppSelector((state) => state.cart);
  const { voidInvoiceResponse, voidInvoiceError, voidInvoiceLoading } =
    useAppSelector((state) => state.invoice);
  const { activeStation, activeStationCashier } = useAppSelector(
    (state) => state.cashierStation
  );
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();

  const customerId = params["id"];
  const cashierId = user?.id;
  const hasSelection =
    !!cartHistorySelectedRows && cartHistorySelectedRows.length > 0;
  const canVoid =
    !!customerId &&
    !!cashierId &&
    !!activeStation?.cashierPoint?.id &&
    !!activeStationCashier?.stationAccessId &&
    !!selectedSession &&
    hasSelection;
  // A bill that's already been refunded has nothing left to refund again —
  // "Refunding Void" would just fail server-side, so block it up front.
  const billAlreadyRefunded = Boolean(cartHistorySelectedRows?.[0]?.isRefunded);
  // A Pay Later bill has no payments to refund either — same reasoning.
  const billHasNoPayments =
    (billPaymentDetails?.payments ?? []).length === 0 ||
    (billPaymentDetails?.totalAmount ?? 0) === 0;
  const refundingVoidDisabled = billAlreadyRefunded || billHasNoPayments;
  // An invoice is "settled" (already paid — via Cheque, Cash, Card or Store
  // Credit, it doesn't matter which) once money has actually been collected
  // against it. Voiding a settled invoice without issuing a refund means the
  // shop keeps money it collected, so that specific choice needs manager
  // sign-off — a refunding void doesn't, since the customer gets their money
  // back either way.
  const isSettledInvoice = !billHasNoPayments;

  // Gates the "No Refunding Void" commit specifically — separate from
  // whatever approval was already used to open this modal — so a no-refund
  // void on a settled invoice always has its own explicit manager sign-off,
  // mirroring the Later-payment gate in payment.tsx / the custom-discount
  // gate in discountModal.tsx.
  const [isNoRefundVoidAccessModalOpen, setIsNoRefundVoidAccessModalOpen] =
    useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setVoidTypeIndex(0);
      setRefundMethod(RefundType.STORE_CREDIT);
      setIsNoRefundVoidAccessModalOpen(false);
    }
  }, [open]);

  // Guard against landing on "Refunding Void" for an already-refunded bill,
  // or a Pay Later bill with nothing to refund (e.g. the selection changes
  // underneath an open modal).
  useEffect(() => {
    if (refundingVoidDisabled && voidTypeIndex === 1) {
      setVoidTypeIndex(0);
    }
  }, [refundingVoidDisabled, voidTypeIndex, setVoidTypeIndex]);

  // Surface why the modal can't proceed instead of failing silently, and
  // close it so the user isn't left looking at a blank/unresponsive modal.
  // Deliberately only re-runs on the open transition (not on every change
  // to hasSelection etc. while the modal stays open) — a successful void
  // clears cartHistorySelectedRows as part of its own cleanup, and that
  // used to re-trigger this same effect and show a spurious "No invoice
  // selected" error right alongside the success message.
  useEffect(() => {
    if (!open) return;
    if (!customerId) {
      snackbar.showSnackbar("Customer not found", "error");
      onClose();
    } else if (!cashierId) {
      snackbar.showSnackbar("Cashier not found", "error");
      onClose();
    } else if (!activeStation?.cashierPoint?.id) {
      snackbar.showSnackbar("Station not found", "error");
      onClose();
    } else if (!activeStationCashier?.stationAccessId) {
      snackbar.showSnackbar("Station session not found", "error");
      onClose();
    } else if (!hasSelection) {
      snackbar.showSnackbar("No invoice selected", "error");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Clear the void request state once it settles. Success/failure feedback
  // is shown where the action is dispatched (handleVoidInvoice), not here,
  // to avoid firing two toasts for the same outcome.
  useEffect(() => {
    if (voidInvoiceResponse || voidInvoiceError) {
      dispatch(resetVoidInvoiceState());
    }
  }, [voidInvoiceResponse, voidInvoiceError, dispatch]);

  if (!canVoid) {
    return null;
  }

  const parsedCustomerId = Number(customerId);

  const handleClose = () => {
    setReason("");
    setVoidTypeIndex(1);
    setRefundMethod(RefundType.STORE_CREDIT);
    onClose();
  };

  const bill = cartHistorySelectedRows[0];
  const billId: number = bill.id;
  const invoiceNumber: string = bill.billId;

  // Entry point for the "Void Invoice" button. A no-refund void on a
  // settled (already-paid) invoice needs a dedicated manager approval before
  // it commits — intercept here and only fall through to commitVoidInvoice
  // once that's been granted. Voiding an unpaid invoice, or a void that
  // includes a refund, doesn't need this extra gate and commits directly
  // (the refund path still has its own pre-existing approvedManagerId check).
  const handleVoidInvoice = () => {
    if (voidTypeIndex === 0 && isSettledInvoice) {
      setIsNoRefundVoidAccessModalOpen(true);
      return;
    }
    commitVoidInvoice();
  };

  const commitVoidInvoice = () => {
    if (voidTypeIndex === 1 && !approvedManagerId) {
      snackbar.showSnackbar(
        "Manager approval is required before refunding a void.",
        "error"
      );
      return;
    }

    const basePayload = {
      billId,
      voidReason: reason,
      cashierId: String(cashierId),
    };

    const sessionId = localStorage.getItem("stationAccessSessionId");

    const refundPayload = {
      ...basePayload,
      ...(voidTypeIndex === 1
        ? {
            refund: {
              refundItems: [
                {
                  billId,
                  refundMethod: refundMethod,
                },
              ],
              customerId: parsedCustomerId,
              cashierPointId: activeStationCashier.cashierPointId,
              stationAccessSessionId: Number(sessionId),
              managerId: approvedManagerId as number,
              reason: "Voiding invoice refund",
            },
          }
        : {}),
    };

    dispatch(voidInvoiceAction(refundPayload))
      .then((res) => {
        const payload = res.payload as
          | { status?: string; message?: string }
          | string
          | undefined;
        const status = typeof payload === "object" ? payload?.status : undefined;
        const message =
          typeof payload === "object" ? payload?.message : undefined;

        if (status === "SUCCESS") {
          dispatch(CartSliceAction.setRefreshCustomerHistory(true));
          // Deselect the just-voided bill so it can't linger in
          // cartHistorySelectedRows[0] and block the Edit/Void/Refund
          // toolbar for the next bill the user tries to select.
          dispatch(CartSliceAction.setCartHistorySelectedRows([]));
          dispatch(CartSliceAction.setBillItemIdList([]));
          onRefundSuccess();
          handleClose();
          snackbar.showSnackbar("Bill voided successfully", "success");
        } else {
          snackbar.showSnackbar(
            message || "Failed to void invoice. Please try again.",
            "error"
          );
        }
      })
      .catch(() => {
        snackbar.showSnackbar("Error voiding invoice", "error");
      });
  };

  const payments: {
    method: string;
    amount: number;
  }[] =
    (billPaymentDetails?.payments ?? []).length > 0
      ? billPaymentDetails?.payments
          .map((payment) => {
            // skip payments with paymentType LATER or MONTHLY
            if (
              payment.paymentType === "LATER" ||
              payment.paymentType === "MONTHLY"
            ) {
              return null;
            }

            return {
              method: paymentTypeLabels(payment.paymentType),
              amount: payment.amount,
            };
          })
          .filter(
            (payment): payment is { method: string; amount: number } =>
              payment !== null
          ) ?? []
      : [
          {
            method: "No Payments",
            amount: 0,
          },
        ];

  return (
    <>
    <CustomModal
      title="Void Invoice"
      openModal={open}
      width={pxToRem(550)}
      height={pxToRem(
        voidTypeIndex === 0
          ? 448
          : refundMethod === RefundType.STORE_CREDIT
          ? 574
          : payments.length === 1
          ? 574
          : payments.length === 2
          ? 600
          : payments.length === 3
          ? 630
          : 650
      )}
      onClose={() => {
        onClose();
        onRefundSuccess();
      }}
    >
      {/* Modal Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: pxToRem(20),
        }}
      >
        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <Box
            sx={{
              width: "100%",
              justifyContent: "flex-start",
              display: "flex",
              padding: "0",
              maxHeight: pxToRem(44),
              borderBottom: `${
                window.innerWidth <= 1280 || window.innerHeight <= 551
                  ? "none"
                  : `${pxToRem(1.5)} solid ${Colors.ACCENT_GRAY_DISABLED}`
              }`,
            }}
          >
            <Tabs
              value={voidTypeIndex}
              onChange={(e, newValue) => setVoidTypeIndex(newValue)}
              TabIndicatorProps={{
                style: { display: "none" },
              }}
              sx={{
                "& .MuiTab-root": {
                  background: Colors.BACKSHADE_GRAY,
                  color: Colors.PRIMARY,
                  height: pxToRem(43),
                  minHeight: pxToRem(43),
                  width: "11.9285714286rem",
                  whiteSpace: "nowrap",
                  textTransform: "none",
                  fontWeight: "medium",
                },
                "& .Mui-selected": {
                  background: Colors.PRIMARY,
                  color: `${Colors.WHITE} !important`,
                },
                "& .MuiTab-root:first-of-type": {
                  borderRadius: "0.71rem 0 0 0",
                },
                "& .MuiTab-root:nth-of-type(2)": {
                  borderRadius: "0 0.71rem 0 0",
                },
              }}
            >
              <Tab
                test-name="No Refunding Void"
                label="No Refunding Void"
                sx={{ fontWeight: "bold", fontSize: "1.142rem" }}
              />
              <Tab
                test-name="Refunding Void"
                label="Refunding Void"
                disabled={refundingVoidDisabled}
                title={
                  billAlreadyRefunded
                    ? "This bill has already been refunded"
                    : billHasNoPayments
                    ? "This bill has no payments to refund"
                    : undefined
                }
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.142rem",
                  "&.Mui-disabled": {
                    color: Colors.ACCENT_GRAY_DISABLED,
                  },
                }}
              />
            </Tabs>
          </Box>
        </Box>

        {/* Content Changed by tabs */}
        {voidTypeIndex === 1 && (
          <Box
            sx={{
              px: pxToRem(6),
              overflowY: `auto`,
              overflowX: `hidden`,
              pt: pxToRem(10),

              "&::-webkit-scrollbar": {
                width: "0.3rem",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ADADAD",
                borderRadius: "0rem",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
                borderRadius: "0rem",
              },
            }}
          >
            <Box
              sx={{
                padding: "0",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: pxToRem(12),
              }}
            >
              {/* Refund Method */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexWrap: "nowrap",
                  width: "100%",
                  gap: pxToRem(25),
                }}
              >
                <Typography
                  sx={{
                    fontSize: pxToRem(18),
                    color: Colors.MAIN_GRAY,
                    whiteSpace: "nowrap",
                    width: pxToRem(200),
                  }}
                >
                  Refund Method:
                </Typography>
                <CustomSelectBox
                  value={refundMethod}
                  onChange={(e) =>
                    setRefundMethod(e.target.value as RefundType)
                  }
                  test-name="refund method"
                  width="100%"
                  sx={{
                    "& .MuiSelect-icon": {
                      backgroundColor: Colors.SECONDARY,
                      color: Colors.WHITE,
                      width: "1.6rem",
                      height: "1.6rem",
                      fontSize: "1.2rem",
                      borderRadius: "0.4rem",
                      top: "calc(50% - 0.8rem)",
                    },
                  }}
                >
                  <MenuItem
                    test-name="Refund to Store Credits"
                    value={RefundType.STORE_CREDIT}
                  >
                    Refund to Store Credits
                  </MenuItem>
                  <MenuItem
                    test-name="Refund to Same Method"
                    value={RefundType.SAME_METHOD}
                  >
                    Refund to Same Method
                  </MenuItem>
                </CustomSelectBox>
              </Box>

              {/* Store Credit Refund Amount */}
              {refundMethod === RefundType.STORE_CREDIT && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    flexWrap: "nowrap",
                    width: "100%",
                    gap: pxToRem(25),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: pxToRem(18),
                      color: Colors.MAIN_GRAY,
                      whiteSpace: "nowrap",
                      width: pxToRem(200),
                    }}
                  >
                    Store Credit Amount:
                  </Typography>
                  <Box
                    sx={{
                      flexGrow: 1,
                      pl: pxToRem(7),
                    }}
                  >
                    <CustomTextBox
                      value={formatCurrency({
                        amount: billPaymentDetails?.totalAmount ?? 0,
                        prefix: "$",
                      })}
                      disabled={true}
                      sx={{
                        width: `calc(100% - ${pxToRem(2)})`,
                        "& input": {
                          background: `${Colors.ACCENT_GRAY} !important`,
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* Refund Amounts */}
              {refundMethod === RefundType.SAME_METHOD && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    flexWrap: "nowrap",
                    width: "100%",
                    gap: pxToRem(25),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: pxToRem(18),
                      color: Colors.MAIN_GRAY,
                      whiteSpace: "nowrap",
                      width: pxToRem(200),
                    }}
                  >
                    Refund Options
                  </Typography>
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      overflowX: "hidden",
                      maxHeight: pxToRem(110),
                      paddingRight: payments.length > 3 ? pxToRem(10) : 0,
                    }}
                  >
                    <ul
                      style={{
                        paddingInline: "0",
                        marginBlock: pxToRem(10),
                        paddingBlock: "0",
                        display: "flex",
                        flexDirection: "column",
                        gap: pxToRem(10),
                        fontSize: pxToRem(16),
                        color: Colors.TEXT_GRAY_DARK,
                      }}
                    >
                      {payments.map((payment, index) => (
                        <li
                          key={index}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginLeft: 0,
                            paddingLeft: 0,
                          }}
                        >
                          <span>{payment.method ?? "Undefined"}:</span>{" "}
                          <strong>
                            {formatCurrency({
                              amount: payment.amount,
                              prefix: "$",
                            })}
                          </strong>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Bottom Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "2rem",
            py: "1rem",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            <Typography
              sx={{
                fontSize: pxToRem(18),
                color: Colors.TEXT_GRAY_DARK,
              }}
            >
              Reason for voiding invoice number <strong>{invoiceNumber}</strong>
              :
            </Typography>
            <CustomTextBox
              value={reason}
              test-name="void reason"
              onChange={(e) => setReason(e.target.value)}
              sx={{
                width: "100%",
                "& .MuiInputBase-input": {
                  width: pxToRem(467),
                  height: pxToRem(69),
                  minHeight: pxToRem(69),
                  maxHeight: pxToRem(69),
                },
              }}
            />
          </Box>

          <CustomButton
            sx={{
              background: Colors.PRIMARY,
              width: `100%`,
              height: pxToRem(64),
              fontSize: pxToRem(24),
              mt: pxToRem(40),
            }}
            test-name="void invoice button"
            onClick={handleVoidInvoice}
            disabled={
              reason.trim() === "" ||
              voidInvoiceLoading ||
              (((billPaymentDetails?.payments ?? []).length === 0 ||
                (billPaymentDetails?.totalAmount ?? 0) === 0) &&
                voidTypeIndex === 1)
            }
          >
            {voidInvoiceLoading ? (
              <CircularProgress
                size={18}
                sx={{
                  color: Colors.ACCENT_GRAY_DISABLED,
                }}
              />
            ) : (
              "Void Invoice"
            )}
          </CustomButton>
        </Box>
      </Box>
    </CustomModal>

    <ManagerCodeModal
      open={isNoRefundVoidAccessModalOpen}
      onClose={() => setIsNoRefundVoidAccessModalOpen(false)}
      title="Manager Approval"
      task="void this paid invoice without issuing a refund"
      onDone={() => {
        setIsNoRefundVoidAccessModalOpen(false);
        commitVoidInvoice();
      }}
      actionType="VOID_NO_REFUND_ACCESS"
      requestedByUserId={user?.id}
      billId={billId}
    />
    </>
  );
};

export default VoidInvoiceModal;
